import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import stylesheet from "../detail-container.stylesheet";
import { withStyles } from '@mui/styles'
import ReactApexChart from "react-apexcharts";
import moment from 'moment';

interface IProps {
	input: {
		data: [string, number][],
		yMin: number,
		yMax: number,
		type: string,
		name?: string
	}
	classes: any
}

const ChartRangeComponent: React.FC<IProps> = ({ input, classes }) => {
	const timeRange = { 'one_month': '1M', 'six_month': '6M', 'one_yr': '1Y', 'all': 'All' } as any
	const startDate = input.data.length > 0 ? input.data[0][0] : undefined
	const [range, setRange] = useState('all') as any
	const initData = input.data

	function calculateTrendLine(chartData: [string, number][]) {
		const flat = () => chartData.map(([date, val]) => [date, val] as [string, number])
		if (chartData.length <= 1) return flat()
		const n = chartData.length;
		// Normalize timestamps against the first point so x-values stay small.
		// Using raw millisecond timestamps (~1.7e12) and squaring them (~2.9e24)
		// exceeds JS double precision, which makes the denominator a tiny non-zero
		// residual when all dates are identical (e.g. multiple readings on the same
		// day). Dividing by that produces an Infinity/NaN slope, and ApexCharts then
		// throws a "parser Error" on the resulting SVG path.
		const baseX = new Date(chartData[0][0]).getTime();
		const xs = chartData.map(([date]) => new Date(date).getTime() - baseX);

		const sumX = xs.reduce((acc, x) => acc + x, 0);
		const sumY = chartData.reduce((acc, [, val]) => acc + val, 0)
		const sunXY = chartData.reduce((acc, [, val], i) => acc + xs[i] * val, 0);
		const sumX2 = xs.reduce((acc, x) => acc + x * x, 0);

		const denominator = n * sumX2 - Math.pow(sumX, 2);
		if (denominator === 0) return flat()

		const m = (n * sunXY - sumX * sumY) / denominator;
		const b = (sumY - m * sumX) / n;
		if (!Number.isFinite(m) || !Number.isFinite(b)) return flat()

		return chartData.map(([date], i) => {
			const trendValue = m * xs[i] + b;
			return [date, Number.isFinite(trendValue) ? trendValue : null] as [string, number]
		});
	};

	const [state, setState] = useState({
		series: [
			{
				data: initData,
				name: input.name || ""
			},
			{
				name: "Trendline",
				data: calculateTrendLine(initData),
				type: "line",
				color: "black",
			}
		],
		options: {
			chart: {
				id: 'area-datetime',
				type: 'area',
				height: 350,
				toolbar: { show: false },
				zoom: {
					autoScaleYaxis: true,
					enabled: false
				}
			},
			dataLabels: {
				enabled: false
			},
			markers: {
				size: [2.5, 0],
				fillColors: ['orange']
			},
			xaxis: {
				crosshairs: false,
				type: 'datetime',
				min: startDate ? new Date(startDate).getTime() : undefined,
				tickAmount: Math.min(10, Math.max(4, initData.length + 1)),
				labels: {
					rotate: -40,
					rotateAlways: true,
					formatter: function (val: any, timestamp: any) {
						return moment(new Date(timestamp)).format("DD MMM YY")
					}
				},
				tooltip: {
					enabled: false
				}
			},
			noData: {
				text: "No Data",
			},
			yaxis: {
				labels: {
					formatter: function (val: any) {
						return val % 1 === 0 ? val : val.toFixed(2)
					},
					style: {
						color: "#8e8da4"
					},
					offsetX: 0,
					min: 70,
					show: true,
				},
				axisBorder: {
					show: true
				},
				axisTicks: {
					show: true
				}
			},
			stroke: {
				show: true,
				curve: ['smooth', "straight"],
				width: 1.2,
				dashArray: [0, 2]
			},
			tooltip: {
				enabledOnSeries: [0],
				x: {
					format: 'dd MMM yy'
				},
				intersect: true,
				shared: false
			},
			fill: {
				type: ['gradient', ""],
				colors: ['var(--color-primary)'],
				gradient: {
					shadeIntensity: 1,
					opacityFrom: 0.7,
					opacityTo: 0.1,
					stops: [0, 100]
				}
			},
			grid: {
				show: false
			},
			legend: {
				show: false
			}
		},
	}) as any

	useEffect(() => {
		setRange('all')
		setState((preState: any) => {
			return {
				...preState,
				options: {
					...preState.options,
					xaxis: {
						...preState.options.xaxis,
						min: startDate ? new Date(startDate).getTime() : undefined
					},
				},
				series: [
					{
						data: input.data,
						name: input.name || ""
					},
					{
						...preState.series[1],
						data: calculateTrendLine(input.data)
					}
				]
			}
		})
	}, [input])

	const updateChartData = (minDate: any, data: any) => {
		setState((preState: any) => {
			return {
				...preState,
				options: {
					...preState.options,
					xaxis: {
						...preState.options.xaxis,
						tickAmount: data.length ? Math.min(10, Math.max(4, data.length + 1)) : -1,
						min: new Date(minDate).getTime()
					},
				},
				series: [
					{
						...preState.series[0],
						data: data,
					},
					{
						...preState.series[1],
						data: calculateTrendLine(data),
					}
				]
			}
		})
	}

	const updateData = (timeline: any) => {
		setRange(timeline)
		if (timeline === 'all') {
			updateChartData(startDate, initData)
		} else {
			const month = timeline === 'one_yr' ? 12 : (timeline === 'one_month') ? 1 : 6
			const eDate = moment().format('YYYY-MM-DD')
			const sDate = moment().subtract(month, 'months').format('YYYY-MM-DD');

			const currData = initData.filter((i: any) => i[0] >= sDate && i[0] <= eDate)
			updateChartData(currData[0] ? currData[0][0] : undefined, currData)
		}
	}

	return (
		<Box padding="0.5rem">
			<Box id="chart">
				<Box pl={1}>
					<Stack direction='row' spacing={0.5}>
						{Object.keys(timeRange).map((key: any, i: any) =>
							<Button key={key} variant="outlined" id="one_month"
								onClick={() => updateData(key)}
								className={`${classes.medSubSelectBtn} ${range === key ? classes.medSubSelectActiveBtn : ''}`}
							>
								<Typography className={classes.subHeaderTitle} variant="subtitle2" color="textSecondary">
									<small>{timeRange[key]}</small>
								</Typography>
							</Button>
						)}
					</Stack>
				</Box>
				<Box id="chart-timeline">
					<ReactApexChart options={state.options} series={state.series} type="area" />
				</Box>
			</Box>
		</Box>
	);

}

export default withStyles(stylesheet)(ChartRangeComponent);


